import { useCallback, useMemo } from "react"
import PropTypes from "prop-types"
import { map } from "lodash"
import { useDispatch } from "react-redux"
import { Creators } from "model/apiWrapper"
import { useAccountAlias } from "resources/account"
import { combineTanStackStatuses, OK, ERROR } from "helpers/data-statuses"
import useCombinedQueries from "hooks/combine-queries"
import { useIntradayEnhancement } from "hooks/use-intraday-enhancement"
import { extractFirstGraphqlErrorMessage, rejectOnGraphqlErrors, rejectOnRequestLevelGraphqlErrors } from "./extract-graphql-error-message"
import { useFieldScopedErrorWarning } from "./hooks/use-field-scoped-error-warning"

export default function DataIntegrator({ metrics, Renderer, rendererProps, dataTransform, combine: combineOverride }) {
  const dispatch = useDispatch()
  const account = useAccountAlias()
  const defaultCombine = useCallback(results => {
    const status = combineTanStackStatuses(results)
    const data = status === OK ? map(results, "data") : null
    const errorMessage = status === ERROR ? extractFirstGraphqlErrorMessage(results) : null
    return { status, data, errorMessage }
  }, [])
  const combine = combineOverride ?? defaultCombine

  // Only metrics opting in via `tolerateFieldScopedErrors` resolve on a field-scoped error instead of rejecting — everyone else keeps the strict default.
  // Reset key is a stable string built from query identity, not the metrics array itself — metrics gets
  // a fresh reference on nearly every render, which would otherwise reset the dedup on every UI interaction.
  const gathererResetKey = useMemo(
    () => metrics
      .filter(({ tolerateFieldScopedErrors }) => tolerateFieldScopedErrors)
      .map(({ key }) => JSON.stringify(key ?? []))
      .join("|"),
    [metrics],
  )
  const warnIfDegraded = useFieldScopedErrorWarning(gathererResetKey)

  const queries = useMemo(() => map(metrics, ({ key, query, variables, select, tolerateFieldScopedErrors, fieldScopedErrorWarning }) => ({
    queryKey: key,
    queryFn: ({ signal }) => new Promise((resolve, reject) => {
      const onResolve = tolerateFieldScopedErrors
        ? (result) => {
          warnIfDegraded(result, fieldScopedErrorWarning)
          resolve(result)
        }
        : resolve
      const errorPolicy = tolerateFieldScopedErrors ? rejectOnRequestLevelGraphqlErrors : rejectOnGraphqlErrors
      dispatch(Creators.graphql(account, query, variables, errorPolicy(onResolve, reject), reject, signal))
    }),
    select,
  })), [metrics, account, dispatch, warnIfDegraded])

  const { status, data, errorMessage, ...combineExtras } = useCombinedQueries(queries, combine)
  const { effectiveData, effectiveStatus } = useIntradayEnhancement(data, status, queries.map(q => q.queryKey))
  const transformedData = useMemo(() => {
    if (effectiveStatus === OK && typeof dataTransform === "function") {
      return dataTransform(effectiveData)
    }
    return effectiveData
  }, [effectiveStatus, effectiveData, dataTransform])

  return <Renderer status={effectiveStatus} data={transformedData} errorMessage={errorMessage} {...combineExtras} {...rendererProps} />
}

DataIntegrator.propTypes = {
  metrics: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.arrayOf(PropTypes.string).isRequired,
    query: PropTypes.string.isRequired,
    variables: PropTypes.object.isRequired,
    select: PropTypes.func.isRequired,
    tolerateFieldScopedErrors: PropTypes.bool,
    fieldScopedErrorWarning: PropTypes.string,
  })).isRequired,
  Renderer: PropTypes.func.isRequired,
  rendererProps: PropTypes.object,
  dataTransform: PropTypes.func,
  combine: PropTypes.func,
}
